import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, AlertTriangle, PackageSearch } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { Badge } from '../components/ui/Badge'
import { MediaImage } from '../components/MediaImage'
import { usePermissions } from '../contexts/permissions'
import {
  KIND_LABEL, UNITS, apiError, fmtMoney,
  type IndustryTenant, type MaterialKind, type MaterialRow, type MaterialUnit, type Supplier,
} from '../lib/industria'
import type { Product } from '@wsfull/shared'

const selectClass = 'h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60'

type FormState = {
  code: string; name: string; kind: MaterialKind; unit: MaterialUnit
  ownerTenantId: string; productId: string; lotControlled: boolean
  unitsPerPack: string; minQty: string; reorderQty: string
  defaultSupplierId: string; supplierBarcode: string; notes: string
}

const emptyForm: FormState = {
  code: '', name: '', kind: 'componente', unit: 'UN',
  ownerTenantId: '', productId: '', lotControlled: true,
  unitsPerPack: '', minQty: '0', reorderQty: '',
  defaultSupplierId: '', supplierBarcode: '', notes: '',
}

export function IndustriaMateriaisPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canEdit = can('industria-materiais', 'edit')

  const [search, setSearch] = useState('')
  // Produção e envio são dois almoxarifados na cabeça de quem opera, ainda que
  // uma tabela só no banco. Misturar frasco com fita atrapalha a única pergunta
  // que a tela responde: o que eu tenho e o que está parado.
  const [escopo, setEscopo] = useState<'producao' | 'envio' | 'todos'>('producao')
  const [owner, setOwner] = useState('')
  const [includeInactive, setIncludeInactive] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [error, setError] = useState<string | null>(null)

  const { data: tenants = [] } = useQuery<IndustryTenant[]>({
    queryKey: ['industria', 'tenants'],
    queryFn: async () => (await api.get('/materials/tenants')).data,
  })

  const { data: suppliers = [] } = useQuery<Supplier[]>({
    queryKey: ['industria', 'suppliers'],
    queryFn: async () => (await api.get('/suppliers')).data,
    enabled: canEdit,
  })

  // Rótulo/embalagem pode apontar para um SKU — só faz sentido dentro do dono.
  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ['products', form.ownerTenantId],
    queryFn: async () => (await api.get('/products', { params: { tenantId: form.ownerTenantId } })).data,
    enabled: modalOpen && !!form.ownerTenantId,
  })

  const { data: todos = [], isLoading } = useQuery<MaterialRow[]>({
    queryKey: ['industria', 'materials', search, owner, includeInactive],
    queryFn: async () => (await api.get('/materials', {
      params: {
        search: search || undefined,
        ownerTenantId: owner || undefined,
        includeInactive: includeInactive ? 'true' : undefined,
      },
    })).data,
  })

  const daProducao = todos.filter(m => m.kind !== 'embalagem_envio')
  const doEnvio = todos.filter(m => m.kind === 'embalagem_envio')
  const materials = escopo === 'producao' ? daProducao : escopo === 'envio' ? doEnvio : todos

  const abas: { key: typeof escopo; label: string; hint: string; n: number }[] = [
    { key: 'producao', label: 'Produção', hint: 'componente e conteúdo — consumidos pela ordem', n: daProducao.length },
    { key: 'envio', label: 'Envio', hint: 'embalagem — consumida pelo pedido expedido', n: doEnvio.length },
    { key: 'todos', label: 'Todos', hint: 'o almoxarifado inteiro', n: todos.length },
  ]

  const createMutation = useMutation({
    mutationFn: async (body: FormState) => (await api.post('/materials', {
      code: body.code.trim(),
      name: body.name.trim(),
      kind: body.kind,
      unit: body.unit,
      ownerTenantId: body.ownerTenantId || null,
      productId: body.productId || null,
      lotControlled: body.lotControlled,
      unitsPerPack: body.unitsPerPack ? Number(body.unitsPerPack) : null,
      minQty: Number(body.minQty || 0),
      reorderQty: body.reorderQty ? Number(body.reorderQty) : null,
      defaultSupplierId: body.defaultSupplierId || null,
      supplierBarcode: body.supplierBarcode.trim() || null,
      notes: body.notes.trim() || null,
    })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['industria', 'materials'] })
      setModalOpen(false)
      setForm(emptyForm)
    },
    onError: (e) => setError(apiError(e, 'Não foi possível salvar o material')),
  })

  const belowMin = materials.filter(m => m.belowMin).length

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Materiais</h2>
          <p className="text-sm text-gray-500">
            Almoxarifado da indústria — saldo por dono, nunca somado entre marcas
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => { setForm(emptyForm); setError(null); setModalOpen(true) }}>
            <Plus className="h-4 w-4" /> Novo material
          </Button>
        )}
      </div>

      {/* Segregação por consumidor, não por tipo: é o que separa os dois
          almoxarifados na prática. */}
      <div className="flex flex-wrap gap-2">
        {abas.map(a => (
          <button
            key={a.key}
            type="button"
            onClick={() => setEscopo(a.key)}
            title={a.hint}
            className={`rounded-xl border px-4 py-2 text-left transition-colors ${
              escopo === a.key
                ? 'border-blue-300 bg-blue-50/60 ring-1 ring-blue-200'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <span className="text-sm font-medium text-gray-900">{a.label}</span>
            <span className="ml-2 font-mono text-xs text-gray-500">{a.n}</span>
            <span className="block text-xs text-gray-500">{a.hint}</span>
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <label className="mb-1 block text-sm font-medium text-gray-700">Buscar</label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                className="pl-9"
                placeholder="Código ou nome"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="w-56">
            <label className="mb-1 block text-sm font-medium text-gray-700">Dono</label>
            <select className={selectClass} value={owner} onChange={e => setOwner(e.target.value)}>
              <option value="">Todos</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <label className="flex h-10 items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300"
              checked={includeInactive}
              onChange={e => setIncludeInactive(e.target.checked)}
            />
            Incluir inativos
          </label>
        </CardContent>
      </Card>

      {belowMin > 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {belowMin} material(is) abaixo do mínimo.
        </div>
      )}

      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">
            {materials.length} material(is)
          </span>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : materials.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <PackageSearch className="mx-auto mb-3 h-8 w-8 text-gray-300" />
              <p className="text-sm text-gray-400">
                Nenhum material — cadastre o primeiro e receba pelo menu Recebimento
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="w-14 px-4 py-2.5 text-left font-medium">Foto</th>
                    <th className="px-4 py-2.5 text-left font-medium">Material</th>
                    <th className="px-4 py-2.5 text-left font-medium">Tipo</th>
                    <th className="px-4 py-2.5 text-left font-medium">Dono</th>
                    <th className="px-4 py-2.5 text-right font-medium">Saldo</th>
                    <th className="px-4 py-2.5 text-right font-medium">Mínimo</th>
                    <th className="px-4 py-2.5 text-right font-medium">Cobertura</th>
                    <th className="px-4 py-2.5 text-right font-medium">Custo médio</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {materials.map(m => (
                    <tr
                      key={m.id}
                      onClick={() => navigate(`/industria/materiais/${m.id}`)}
                      className="cursor-pointer transition-colors hover:bg-blue-50/40"
                    >
                      <td className="px-4 py-2.5">
                        <MediaImage mediaId={m.photoMediaId} className="h-9 w-9 rounded-lg" />
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="font-medium text-gray-900">{m.name}</span>
                        <span className="block font-mono text-xs text-gray-400">
                          {m.code}
                          {m.productSku ? ` · SKU ${m.productSku}` : ''}
                          {!m.active ? ' · inativo' : ''}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">
                        {KIND_LABEL[m.kind]}
                        {!m.lotControlled && <span className="block text-xs text-gray-400">sem lote</span>}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{m.ownerName ?? 'WS (próprio)'}</td>
                      <td className="px-4 py-2.5 text-right font-mono">
                        <span className={m.quantity < 0 ? 'font-semibold text-red-600' : 'text-gray-900'}>
                          {m.quantity.toLocaleString('pt-BR')}
                        </span>
                        <span className="ml-1 text-xs text-gray-400">{m.unit}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {m.minQty > 0 ? (
                          m.belowMin
                            ? <Badge variant="warning">{m.minQty.toLocaleString('pt-BR')}</Badge>
                            : <span className="font-mono text-gray-500">{m.minQty.toLocaleString('pt-BR')}</span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {m.coverageDays == null ? (
                          <span className="text-gray-300">—</span>
                        ) : (
                          <span className={m.coverageDays <= 7 ? 'font-semibold text-red-600' : 'text-gray-700'}>
                            {m.coverageDays} dia{m.coverageDays === 1 ? '' : 's'}
                            <span className="block text-xs font-normal text-gray-400">
                              {m.dailyUse.toLocaleString('pt-BR')}/dia
                            </span>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-600">
                        {m.avgCost === undefined ? '—' : fmtMoney(m.avgCost)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal title="Novo material" open={modalOpen} onClose={() => setModalOpen(false)} size="lg">
        <form
          className="space-y-4"
          onSubmit={e => { e.preventDefault(); setError(null); createMutation.mutate(form) }}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Código"
              value={form.code}
              onChange={e => setForm({ ...form, code: e.target.value })}
              placeholder="FRASCO-50ML"
              required
            />
            <Input
              label="Nome"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Garrafa de vidro 50 ml"
              required
            />
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Tipo</label>
              <select
                className={selectClass}
                value={form.kind}
                onChange={e => setForm({ ...form, kind: e.target.value as MaterialKind })}
              >
                {Object.entries(KIND_LABEL).map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Unidade</label>
              <select
                className={selectClass}
                value={form.unit}
                onChange={e => setForm({ ...form, unit: e.target.value as MaterialUnit })}
              >
                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700">Dono</label>
              <select
                className={selectClass}
                value={form.ownerTenantId}
                onChange={e => setForm({ ...form, ownerTenantId: e.target.value, productId: '' })}
              >
                <option value="">Catálogo compartilhado — o dono vem no lote</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Escolha a marca só quando o material for exclusivo dela (rótulo, caixa
                personalizada). Item comum a várias marcas fica sem dono aqui.
              </p>
            </div>
            {!!form.ownerTenantId && (
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">Produto vinculado (opcional)</label>
                <select
                  className={selectClass}
                  value={form.productId}
                  onChange={e => setForm({ ...form, productId: e.target.value })}
                >
                  <option value="">Nenhum</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
                </select>
              </div>
            )}
            <Input
              label="Mínimo"
              type="number"
              min={0}
              value={form.minQty}
              onChange={e => setForm({ ...form, minQty: e.target.value })}
            />
            <Input
              label="Lote de compra sugerido"
              type="number"
              min={1}
              value={form.reorderQty}
              onChange={e => setForm({ ...form, reorderQty: e.target.value })}
            />
            <Input
              label="Unidades por pacote"
              type="number"
              min={1}
              value={form.unitsPerPack}
              onChange={e => setForm({ ...form, unitsPerPack: e.target.value })}
              placeholder="rolo com 1000 etiquetas"
            />
            <Input
              label="Código de barras do fornecedor"
              value={form.supplierBarcode}
              onChange={e => setForm({ ...form, supplierBarcode: e.target.value })}
            />
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700">Fornecedor habitual</label>
              <select
                className={selectClass}
                value={form.defaultSupplierId}
                onChange={e => setForm({ ...form, defaultSupplierId: e.target.value })}
              >
                <option value="">Nenhum</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300"
              checked={form.lotControlled}
              onChange={e => setForm({ ...form, lotControlled: e.target.checked })}
            />
            Controla lote
            <span className="text-xs text-gray-500">
              (sem isso a rastreabilidade do componente não fecha)
            </span>
          </label>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Observações</label>
            <textarea
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60"
              rows={2}
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={createMutation.isPending}>Cadastrar</Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
